import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Inject, Optional, ChangeDetectorRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatLegacySliderChange as MatSliderChange } from '@angular/material/legacy-slider';
import { CAMERA_REF } from './camera-ref_token';
import { CameraRef } from './camera-ref';
import { CAMERA_CONTEXTO, CameraConviteContexto } from './camera-contexto';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/shared/auth/auth.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { AbConfigSkin } from 'app/abstract/full-pages/config-skin/config-skin.abstract';
import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { PostHogEventEnum } from 'enums/posthog-event.enum';
import { PostHogProperties } from 'app/services/posthog/posthog-properties.types';
import {
  aplicarHistereseGate,
  avaliarGateFacial,
  descreverOrientacao,
  extrairCaixaRosto,
  extrairScoreDeteccao,
  traduzirMotivoBloqueio,
  EstadoHistereseGate,
  MotivoBloqueioFacial,
  ResultadoGateFacial
} from './facial-gate';
import {
  aplicarExigenciaMargemPsim,
  atendeMargemPsim,
  calcularRecorteFacial,
  margemLateralEfetiva,
  margemVerticalEfetiva,
  recorteAtendeMargemPsim,
  recorteFrameInteiro
} from './facial-crop';

export enum Rotation {
  TOP,
  LEFT,
  BOTTOM,
  RIGHT
}

declare const faceapi: any;
declare const navigator: any;

type MotivoInternoFacial = MotivoBloqueioFacial | 'AGUARDANDO_INICIALIZACAO';
type OrigemCapturaFacial = 'validada';

const MOTIVO_INDETERMINADO = 'INDETERMINADO';
const ERRO_TIMEOUT_INFERENCIA = 'TIMEOUT_INFERENCIA';

@Component({
  selector: 'camera-convite',
  templateUrl: './camera-convite.component.html',
  styleUrls: ['./camera-convite.component.scss']
})
export class CameraConviteComponent implements OnInit, OnDestroy {

  @ViewChild('canvas')
  public canvas: ElementRef;
  public canvasDetect: HTMLCanvasElement;
  @ViewChild('video')
  public video: ElementRef;
  public canTorch: boolean;
  public torchOn: boolean;
  public canZoom: boolean;
  public zoomMax: number;
  public zoomMin: number;
  public zoomStep: number;
  public zoomValue: number;
  public canSwapCamera: boolean;
  public pictureData: string;
  private stream: MediaStream;
  enableCapture: boolean = false;
  private animationFrameId: number | null = null;
  private countDetections: number = 0;
  private detection: any;
  skin: any;
  imgLogo: string;
  url = environment.urlApi;
  private resizedDetections: any[];
  shouldFaceUser: any = true;
  stop: boolean = false;

  mensagemOrientacao: string = 'FACIAL_ORIENTACAO_PREPARANDO';
  facialObrigatoria: boolean = true;

  private readonly INTERVALO_MINIMO_MENSAGEM_MS = 700;
  private readonly INTERVALO_ENTRE_DETECCOES_MS = 120;
  private readonly ATRASO_AGUARDANDO_VIDEO_MS = 200;
  private readonly INPUT_SIZE_DETECTOR = 320;
  private readonly LIMIAR_ESPERA_PROLONGADA_MS = 15000;
  private readonly LIMIAR_LOOP_TRAVADO_MS = 20000;
  private readonly TIMEOUT_INFERENCIA_MS = 20000;
  private readonly TIMEOUTS_TOLERADOS = 2;

  private modeloCarregado: boolean = false;
  private modeloPromise: Promise<void> | null = null;
  private modoFallbackManual: boolean = false;
  private loopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private esperaProlongadaTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private loopTravadoTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private motivoAtual: MotivoInternoFacial = 'AGUARDANDO_INICIALIZACAO';
  private mensagemAtualizadaEm: number = 0;
  private abertaEm: number = 0;
  private liberadaEm: number | null = null;
  private rostoDetectado: boolean = false;
  private ultimoScore: number | null = null;
  private ultimaDeteccaoEm: number = 0;
  private duracaoInferenciaMs: number | null = null;
  private estadoHisterese: EstadoHistereseGate = { liberado: false, bloqueiosConsecutivos: 0 };
  private fotoConfirmada: boolean = false;
  private saidaRegistrada: boolean = false;
  private falhaDeteccaoRegistrada: boolean = false;
  private esperaProlongadaRegistrada: boolean = false;
  private loopTravadoRegistrado: boolean = false;
  private descarteInferenciaRegistrado: boolean = false;
  private timeoutsConsecutivos: number = 0;
  private contagemMotivos: Record<string, number> = {};

  private readonly destroy$ = new Subject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(CAMERA_REF) private cameraRef: CameraRef,
    private authService: AuthService,
    private configSkinService: AbConfigSkin,
    private http: HttpClient,
    private ref: ChangeDetectorRef,
    private posthog: AbPostHog,
    @Optional() @Inject(CAMERA_CONTEXTO) private contexto: CameraConviteContexto | null
  ) {
    this.facialObrigatoria = this.contexto?.facialObrigatoria !== false;
  }

  public ngOnInit(): void {
    this.abertaEm = Date.now();
    this.carregarSkinStorage();
    this.carregarModelo();
    this.setupStream();
  }

  public ngOnDestroy(): void {
    if (!this.fotoConfirmada && !this.saidaRegistrada) {
      this.rastrear(PostHogEventEnum.FACIAL_ABANDONADO, {
        origem: 'saida_da_tela',
        rosto_detectado: this.rostoDetectado,
        liberou_captura: this.liberadaEm !== null,
        motivo_predominante: this.motivoPredominante(),
        ultimo_motivo: this.motivoAtual,
        ultimo_score: this.ultimoScore,
        deteccoes_executadas: this.countDetections
      });
    }
    this.cleanUp();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private async carregarModelo(): Promise<void> {
    const inicio = Date.now();
    this.modeloPromise = faceapi.nets.tinyFaceDetector.loadFromUri('assets/models');
    try {
      await this.modeloPromise;
      this.modeloCarregado = true;
      this.rastrear(PostHogEventEnum.FACIAL_MODELO_CARREGADO, { duracao_ms: Date.now() - inicio });
    } catch (err) {
      this.modeloCarregado = false;
      console.error('[CameraConvite] falha ao carregar o modelo de deteccao facial', err);
      this.rastrear(PostHogEventEnum.FACIAL_MODELO_FALHOU, {
        duracao_ms: Date.now() - inicio,
        erro: this.descreverErro(err)
      });
      this.modoFallbackManual = true;
    }
  }

  public async swapCamera(): Promise<void> {
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
      this.shouldFaceUser = !this.shouldFaceUser
      this.cleanUp();
      this.setupStream();
    }
  }

  public takePicture(): void {
    if (!this.enableCapture) {
      return;
    }
    if (this.rostoProximoDemais()) {
      this.aplicarMensagem('FACIAL_ORIENTACAO_AFASTE', true);
      this.setEnableCapture(false);
      return;
    }
    this.capturar('validada');
  }

  private rostoProximoDemais(): boolean {
    const caixa = extrairCaixaRosto(this.detection);
    if (!caixa) {
      return false;
    }
    const video: HTMLVideoElement = this.video.nativeElement;
    return !recorteAtendeMargemPsim(caixa, { width: video.videoWidth, height: video.videoHeight });
  }


  private capturar(origem: OrigemCapturaFacial): void {
    const video: HTMLVideoElement = this.video.nativeElement;
    const dimensoes = { width: video.videoWidth, height: video.videoHeight };
    const caixa = extrairCaixaRosto(this.detection);
    const regiao = calcularRecorteFacial(caixa, dimensoes) ?? recorteFrameInteiro(dimensoes);

    const canvas: HTMLCanvasElement = this.document.createElement('canvas');
    canvas.width = regiao.sw;
    canvas.height = regiao.sh;

    const context = canvas.getContext('2d');
    if (this.shouldFaceUser) {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }

    context.drawImage(
      video,
      regiao.sx,
      regiao.sy,
      regiao.sw,
      regiao.sh,
      0,
      0,
      canvas.width,
      canvas.height
    );
    this.pictureData = canvas.toDataURL('image/jpeg');
    this.stopCanvas();
    this.rastrear(PostHogEventEnum.FACIAL_FOTO_CAPTURADA, {
      tempo_ate_captura_ms: Date.now() - this.abertaEm,
      origem,
      margem_lateral: caixa ? Number(margemLateralEfetiva(regiao, caixa).toFixed(2)) : null,
      margem_vertical: caixa ? Number(margemVerticalEfetiva(regiao, caixa).toFixed(2)) : null,
      atende_margem_psim: caixa ? atendeMargemPsim(regiao, caixa) : null,
      largura_recorte: regiao.sw,
      altura_recorte: regiao.sh
    });
  }

  public retake(): void {
    this.pictureData = null;
    this.mensagemOrientacao = 'FACIAL_ORIENTACAO_PREPARANDO';
    this.mensagemAtualizadaEm = 0;
    this.setEnableCapture(false);
    this.startCanvas(this.canvas.nativeElement, this.video.nativeElement);
  }

  public use(): void {
    this.fotoConfirmada = true;
    this.rastrear(PostHogEventEnum.FACIAL_FOTO_CONFIRMADA, {
      tempo_total_ms: Date.now() - this.abertaEm
    });
    this.cameraRef.close(this.pictureData);
  }

  public close(): void {
    this.cameraRef.close();
  }

  public pular(): void {
    this.rastrear(PostHogEventEnum.FACIAL_ABANDONADO, {
      origem: 'botao_continuar_sem_cadastrar',
      rosto_detectado: this.rostoDetectado,
      liberou_captura: this.liberadaEm !== null,
      motivo_predominante: this.motivoPredominante(),
      ultimo_motivo: this.motivoAtual
    });
    this.saidaRegistrada = true;
    this.cameraRef.close();
  }

  public setEnableCapture(enable): void {
    this.enableCapture = enable
    this.ref?.detectChanges()
  }

  public onZoomChange(event: MatSliderChange): void {
    this.zoomValue = event.value;
    if (this.stream) {
      const [videoTrack] = this.stream.getVideoTracks();
      if (videoTrack) {
        videoTrack.applyConstraints({ advanced: [{ zoom: event.value } as any] });
      }
    }
  }

  private async setupStream(): Promise<void> {
    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: this.shouldFaceUser ? 'user' : 'environment',
        width: 600,
        height: 500
      }, audio: false
    };
    navigator.mediaDevices.getUserMedia(
      constraints).then(
        (stream) => {
          this.stream = stream;
          if (this.stream) {
            // start video
            this.stopCanvas();
            const video = this.video.nativeElement
            video.onloadedmetadata = (e) => {
              video.style.zIndex = "1"
              const canvas: HTMLCanvasElement = this.canvas.nativeElement;
              canvas.style.zIndex = "2"
              const displaySize = { width: video.videoWidth, height: video.videoHeight };
              faceapi.matchDimensions(canvas, displaySize);
              this.rastrear(PostHogEventEnum.FACIAL_CAMERA_ABERTA, {
                video_width: displaySize.width,
                video_height: displaySize.height,
                orientacao: descreverOrientacao(displaySize),
                camera_frontal: !!this.shouldFaceUser
              });
              this.startCanvas(canvas, video);

              if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
                this.canSwapCamera = true;
                this.ref.detectChanges()
              }

            }
            this.addStreamToVideo(this.stream, video);
          }
        }).catch(
          (error) => {
            console.error('[CameraConvite] falha ao acessar a camera', error);
            this.rastrear(PostHogEventEnum.FACIAL_CAMERA_FALHOU, { erro: this.descreverErro(error) });
            this.cameraRef.reject(error);
          }
        );
  }

  private async updateCanvas(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
    if (this.stop) {
      return;
    }

    try {
      if (!canvas || !video || !video.videoWidth) {
        this.aplicarResultadoGate(avaliarGateFacial(null, null));
        return;
      }

      const displaySize = this.desenharMascara(canvas, video);

      if (this.modoFallbackManual) {
        this.aplicarFallbackManual();
        return;
      }

      if (this.deveDetectar()) {
        this.detection = await this.detectarRosto(video);
      }

      if (this.stop) {
        return;
      }

      this.ultimoScore = extrairScoreDeteccao(this.detection);
      const caixa = extrairCaixaRosto(this.detection);
      this.aplicarResultadoGate(
        aplicarExigenciaMargemPsim(avaliarGateFacial(caixa, displaySize), caixa, displaySize)
      );
    } catch (err) {
      this.tratarFalhaDeteccao(err);
    } finally {
      this.reagendarLoop(canvas, video);
    }
  }

  private desenharMascara(canvas: HTMLCanvasElement, video: HTMLVideoElement): { width: number; height: number } {
    const context: CanvasRenderingContext2D = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const displaySize = { width: canvas.width, height: canvas.height };
    context.beginPath();
    context.rect(displaySize.width, 0, -displaySize.width, displaySize.height);
    context.ellipse(displaySize.width / 2, displaySize.height / 2, displaySize.height / 4, displaySize.height / 3, 0, 0, 2 * Math.PI);
    context.fillStyle = "rgba(0, 0, 0, 0.5)";
    context.fill('evenodd');

    if (this.enableCapture) {
      context.beginPath();
      context.ellipse(displaySize.width / 2, displaySize.height / 2, displaySize.height / 4, displaySize.height / 3, 0, 0, 2 * Math.PI);
      context.strokeStyle = "#00FF00";
      context.stroke();
    }

    return displaySize;
  }

  private deveDetectar(): boolean {
    return Date.now() - this.ultimaDeteccaoEm >= this.INTERVALO_ENTRE_DETECCOES_MS;
  }

  private async detectarRosto(video: HTMLVideoElement): Promise<unknown> {
    if (!this.modeloCarregado && this.modeloPromise) {
      try {
        await this.modeloPromise;
      } catch {
        return null;
      }
    }
    if (!this.modeloCarregado) {
      return null;
    }

    const inicio = Date.now();
    const deteccao = await this.comLimiteDeTempo(
      faceapi.detectSingleFace(
        video,
        new faceapi.TinyFaceDetectorOptions({ inputSize: this.INPUT_SIZE_DETECTOR })
      )
    );
    this.timeoutsConsecutivos = 0;
    this.ultimaDeteccaoEm = Date.now();
    this.duracaoInferenciaMs = this.ultimaDeteccaoEm - inicio;
    this.countDetections++;

    return deteccao;
  }

  private comLimiteDeTempo<T>(promessa: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const limite = setTimeout(
        () => reject(new Error(ERRO_TIMEOUT_INFERENCIA)),
        this.TIMEOUT_INFERENCIA_MS
      );
      promessa.then(
        valor => {
          clearTimeout(limite);
          resolve(valor);
        },
        erro => {
          clearTimeout(limite);
          reject(erro);
        }
      );
    });
  }

  private aplicarResultadoGate(resultado: ResultadoGateFacial): void {
    this.contagemMotivos[resultado.motivo] = (this.contagemMotivos[resultado.motivo] ?? 0) + 1;
    this.estadoHisterese = aplicarHistereseGate(this.estadoHisterese, resultado);
    const liberado = this.estadoHisterese.liberado;

    if (!this.rostoDetectado && resultado.motivo !== 'SEM_ROSTO' && resultado.motivo !== 'VIDEO_INDISPONIVEL') {
      this.rostoDetectado = true;
      this.rastrear(PostHogEventEnum.FACIAL_ROSTO_DETECTADO, {
        tempo_ate_deteccao_ms: Date.now() - this.abertaEm,
        primeiro_motivo: resultado.motivo,
        score_deteccao: this.ultimoScore,
        duracao_inferencia_ms: this.duracaoInferenciaMs
      });
    }

    if (liberado && this.liberadaEm === null) {
      this.liberadaEm = Date.now();
      this.rastrear(PostHogEventEnum.FACIAL_CAPTURA_LIBERADA, {
        tempo_ate_verde_ms: this.liberadaEm - this.abertaEm,
        score_deteccao: this.ultimoScore,
        duracao_inferencia_ms: this.duracaoInferenciaMs
      });
    }

    this.motivoAtual = resultado.motivo;
    const mensagem = liberado
      ? 'FACIAL_ORIENTACAO_PRONTO'
      : traduzirMotivoBloqueio(resultado.motivo, resultado.desvioHorizontal, !!this.shouldFaceUser);
    const mensagemMudou = this.aplicarMensagem(mensagem, liberado);

    if (liberado !== this.enableCapture) {
      this.setEnableCapture(liberado);
      return;
    }
    if (mensagemMudou) {
      this.ref?.detectChanges();
    }
  }

  private aplicarFallbackManual(): void {
    const mensagemMudou = this.aplicarMensagem('FACIAL_ORIENTACAO_MANUAL', true);

    if (!this.enableCapture) {
      this.liberadaEm = Date.now();
      this.rastrear(PostHogEventEnum.FACIAL_CAPTURA_LIBERADA, {
        modo: 'fallback_manual',
        tempo_ate_verde_ms: this.liberadaEm - this.abertaEm
      });
      this.setEnableCapture(true);
      return;
    }
    if (mensagemMudou) {
      this.ref?.detectChanges();
    }
  }

  private aplicarMensagem(mensagem: string, imediata: boolean): boolean {
    if (mensagem === this.mensagemOrientacao) {
      return false;
    }
    const agora = Date.now();
    if (!imediata && agora - this.mensagemAtualizadaEm < this.INTERVALO_MINIMO_MENSAGEM_MS) {
      return false;
    }
    this.mensagemOrientacao = mensagem;
    this.mensagemAtualizadaEm = imediata ? 0 : agora;
    return true;
  }

  private tratarFalhaDeteccao(err: unknown): void {
    if (err instanceof Error && err.message === ERRO_TIMEOUT_INFERENCIA) {
      this.tratarInferenciaDescartada();
      return;
    }
    if (this.falhaDeteccaoRegistrada) {
      return;
    }
    this.falhaDeteccaoRegistrada = true;
    this.modoFallbackManual = true;
    console.error('[CameraConvite] falha no loop de deteccao facial', err);
    this.rastrear(PostHogEventEnum.FACIAL_DETECCAO_FALHOU, {
      erro: this.descreverErro(err),
      tempo_desde_abertura_ms: Date.now() - this.abertaEm
    });
  }

  private tratarInferenciaDescartada(): void {
    this.timeoutsConsecutivos++;
    this.ultimaDeteccaoEm = Date.now();

    if (!this.descarteInferenciaRegistrado) {
      this.descarteInferenciaRegistrado = true;
      console.error('[CameraConvite] inferencia facial descartada por tempo limite');
      this.rastrear(PostHogEventEnum.FACIAL_INFERENCIA_DESCARTADA, {
        duracao_ms: this.TIMEOUT_INFERENCIA_MS,
        descartes_consecutivos: this.timeoutsConsecutivos
      });
    }

    if (this.timeoutsConsecutivos > this.TIMEOUTS_TOLERADOS) {
      this.modoFallbackManual = true;
    }
  }

  private reagendarLoop(canvas: HTMLCanvasElement, video: HTMLVideoElement): void {
    if (this.stop) {
      return;
    }
    if (!video?.videoWidth) {
      this.loopTimeoutId = setTimeout(
        () => this.updateCanvas(canvas, video),
        this.ATRASO_AGUARDANDO_VIDEO_MS
      );
      return;
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = requestAnimationFrame(() => this.updateCanvas(canvas, video));
  }

  private motivoPredominante(): string {
    const entradas = Object.entries(this.contagemMotivos);
    if (!entradas.length) {
      return MOTIVO_INDETERMINADO;
    }
    return entradas.sort((a, b) => b[1] - a[1])[0][0];
  }

  private descreverErro(err: unknown): string {
    if (err instanceof Error) {
      return err.name ? `${err.name}: ${err.message}` : err.message;
    }
    if (err && typeof err === 'object' && 'name' in err) {
      return String((err as { name?: unknown }).name);
    }
    return String(err ?? 'desconhecido');
  }

  private rastrear(evento: PostHogEventEnum, propriedades: PostHogProperties = {}): void {
    this.posthog?.capture(evento, {
      fluxo: 'convidado_facial',
      tenant: this.contexto?.tenant ?? null,
      convite_id: this.contexto?.conviteId ?? null,
      video_width: this.video?.nativeElement?.videoWidth ?? null,
      video_height: this.video?.nativeElement?.videoHeight ?? null,
      tempo_na_tela_ms: this.abertaEm ? Date.now() - this.abertaEm : 0,
      ...propriedades
    });
  }

  private startCanvas(canvas: HTMLCanvasElement, video: HTMLVideoElement): void {
    this.stop = false;
    this.estadoHisterese = { liberado: false, bloqueiosConsecutivos: 0 };
    this.detection = null;
    this.ultimaDeteccaoEm = 0;
    this.timeoutsConsecutivos = 0;
    this.armarVigilancia();
    this.updateCanvas(canvas, video);
  }

  private armarVigilancia(): void {
    this.cancelarVigilancia();
    this.esperaProlongadaTimeoutId = setTimeout(
      () => this.aoAtingirEsperaProlongada(),
      this.LIMIAR_ESPERA_PROLONGADA_MS
    );
    this.loopTravadoTimeoutId = setTimeout(
      () => this.aoAtingirLoopTravado(),
      this.LIMIAR_LOOP_TRAVADO_MS
    );
  }

  private cancelarVigilancia(): void {
    if (this.esperaProlongadaTimeoutId) {
      clearTimeout(this.esperaProlongadaTimeoutId);
      this.esperaProlongadaTimeoutId = null;
    }
    if (this.loopTravadoTimeoutId) {
      clearTimeout(this.loopTravadoTimeoutId);
      this.loopTravadoTimeoutId = null;
    }
  }

  private aoAtingirEsperaProlongada(): void {
    this.esperaProlongadaTimeoutId = null;
    if (this.liberadaEm !== null || this.pictureData) {
      return;
    }

    this.aplicarMensagem('FACIAL_VALIDACAO_DEMORANDO', true);

    if (!this.esperaProlongadaRegistrada) {
      this.esperaProlongadaRegistrada = true;
      this.rastrear(PostHogEventEnum.FACIAL_ESPERA_PROLONGADA, {
        tempo_ms: Date.now() - this.abertaEm,
        deteccoes_executadas: this.countDetections,
        ultimo_motivo: this.motivoAtual
      });
    }

    this.ref?.detectChanges();
  }

  private aoAtingirLoopTravado(): void {
    this.loopTravadoTimeoutId = null;
    if (this.pictureData || Object.keys(this.contagemMotivos).length) {
      return;
    }

    if (!this.loopTravadoRegistrado) {
      this.loopTravadoRegistrado = true;
      console.error('[CameraConvite] loop de deteccao facial sem avaliacao apos o limiar');
      this.rastrear(PostHogEventEnum.FACIAL_LOOP_TRAVADO, {
        tempo_ms: Date.now() - this.abertaEm,
        deteccoes_executadas: this.countDetections
      });
    }

    this.modoFallbackManual = true;
    this.aplicarFallbackManual();
  }

  private stopCanvas(): void {
    this.stop = true;
    this.cancelarVigilancia();
    if (this.loopTimeoutId) {
      clearTimeout(this.loopTimeoutId);
      this.loopTimeoutId = null;
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private addStreamToVideo(stream: MediaStream, video: HTMLVideoElement): void {
    if ('srcObject' in video) {
      video.srcObject = stream;
    } else {
      // @ts-ignore
      video.src = URL.createObjectURL(stream);
    }
    video.play().catch(() => {
      // Autoplay blocked by browser policy (e.g. Chrome Android without user gesture).
      // The video has autoplay + muted attributes as fallback; nothing else to do.
    });
  }

  private stopStream(stream: MediaStream) {
    // @ts-ignore
    if (typeof stream.stop == 'function') {
      // @ts-ignore
      stream.stop();
    }
    stream.getTracks().forEach(track => {
      track.stop();
    })
  }

  private cleanUp(): void {
    this.stopCanvas();
    if (this.video.nativeElement.src) {
      URL.revokeObjectURL(this.video.nativeElement.src);
    }
    if (this.stream) {
      this.stopStream(this.stream);
    }
  }

  private async carregarSkinStorage() {
    this.skin = this.authService.getSkin();
    if (this.skin && this.skin.logoHeader) {
      this.insertValueCustonSkin(this.skin);
      return;
    }
    this.carregarCustomSkin();
  }

  private async carregarCustomSkin() {
    this.getCustomSkin()
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe(skin => {
        if (skin)
          this.insertValueCustonSkin(skin);
      });
  }

  getCustomSkin(): Observable<any> {
    const urlIntegrador = { url: window.location.href.split('//')[1].split('/')[0] }
    return this.post("public/custom_skin/", urlIntegrador);
  }

  public post(endPoint: string, entity: any): Observable<any> {
    return this.http.post(this.url + endPoint, entity);
  }

  private insertValueCustonSkin(skin): void {
    this.skin = skin;
    this.imgLogo = skin.logoHeader;
    if (this.skin.logoHeader) {
      const url = skin.logoHeader.startsWith("http");
      this.skin.logoHeader = (url ? "" : "data:image/jpg;base64,") + skin.logoHeader;
    }
    this.retornarConfigSkinConta();
  }

  private retornarConfigSkinConta() {
    const arrayBody = document.body.classList;
    for (const i in arrayBody) {
      if (arrayBody.hasOwnProperty(i)) {
        if (arrayBody[i].indexOf("body-") != -1) {
          document.getElementById('body-Center').classList.remove(arrayBody[i]);
          document.getElementById('body-Center').classList.add(arrayBody[i]);
        }

        if (arrayBody[i].indexOf("-theme") != -1) {
          document.getElementById('body-Center').classList.remove(arrayBody[i]);
          document.getElementById('body-Center').classList.add(arrayBody[i]);
        }
      }
    }

    this.configSkinService.setThemeSkin(this.skin.cor);
    document.getElementById('body-Center').classList.add("body-" + this.skin.cor);
    document.getElementById('body-Center').classList.add("body-convidado");
  }


}
